/**
 * The series taxonomy.
 *
 * A SERIES is a feeling (danger, wonder, cosmos, cunning animals...).
 * A SEASON inside it is one narrow cluster of that feeling.
 * An article belongs to exactly ONE season, chosen by deterministic keyword
 * scoring - never randomly, never by "whatever is left over".
 *
 * Storage contract (collection_items.position):
 *   position = seasonIndex * 100 + episodeNumber
 *   seasonIndex 0..N-1  -> a real season from this file
 *   seasonIndex 99      -> the rotating "Best of the month" pool
 *
 * Both the assignment job and the UI read this file, so labels and membership
 * can never drift apart.
 */

export const SEASON_STRIDE = 100;
export const BEST_OF_SEASON = 99;
/** Hard product rules the user set. */
export const MAX_EPISODES_PER_SEASON = 10;
export const MAX_PARTS_PER_SEASON = 2; // 10 episodes x 2 parts = 20 max per season
export const MIN_EPISODES_PER_SEASON = 4;
export const BEST_OF_SIZE = 3;

export type SeasonDef = {
  key: string;
  title: string;
  /** Weighted match terms. Phrases (with a space) count double. */
  terms: string[];
  /** If any of these match, this season is skipped for that article. */
  not?: string[];
};

export type SeriesDef = {
  slug: string;
  title: string;
  eyebrow: string;
  description: string;
  /** Category slugs this series naturally draws from. */
  categories: string[];
  seasons: SeasonDef[];
};

export const SERIES: SeriesDef[] = [
  {
    slug: "things-that-can-kill-you",
    categories: ["animals", "survival-disasters", "earth-nature", "technology", "physics-chemistry"],
    title: "Things That Can Kill You",
    eyebrow: "Danger",
    description:
      "The heart-rate series. Predators that hunt people, water you cannot swim in, voltage, storms and drops. Every episode is a way the world can end you.",
    seasons: [
      {
        key: "hunted",
        title: "When You Are The Prey",
        terms: [
          "eat people", "attack", "chase", "hunt dogs", "wild animals sometimes eat", "maul",
          "leopards hunt", "ostriches chase", "lions", "tiger", "jaguar", "big cat", "crocodile",
          "shark", "cassowary", "bear",
        ],
        not: ["fossil", "extinct", "million years"],
      },
      {
        key: "venom",
        title: "Venom, Stings And Bites",
        terms: [
          "venom", "poison", "sting", "toxin", "bite", "cobra", "mamba", "snake", "spider",
          "scorpion", "wasp", "hornet", "bullet ant", "jellyfish", "anthrax",
        ],
      },
      {
        key: "water",
        title: "Water That Kills",
        terms: [
          "drown", "flash flood", "tsunami", "rip current", "silent killer", "lake",
          "quicksand", "peat island", "whirlpool", "riptide", "swim in",
        ],
        not: ["swimming pool"],
      },
      {
        key: "sky",
        title: "Sky Out Of Control",
        terms: [
          "lightning", "tornado", "hurricane", "storm", "cyclone", "hail", "thunder",
          "el nino", "el niño", "heat record", "heat wave", "volcano", "eruption", "fault line", "earthquake",
        ],
      },
      {
        key: "manmade",
        title: "Man-Made Death Traps",
        terms: [
          "power line", "live wire", "electrician", "voltage", "electrocut", "fire truck",
          "kitchen fire", "flooded to stop fires", "well by hand", "shoot flames",
          "rescue is a death", "rebuild where disaster", "city's heat",
        ],
      },
      {
        key: "heights",
        title: "Heights, Cliffs And Falling",
        terms: [
          "cliff", "falling", "fall", "height", "climbers", "sheer rock", "gravity on sheer",
          "mountain goat", "sinkhole", "swallows whole houses", "earth swallows",
        ],
      },
    ],
  },
  {
    slug: "animals-with-a-plan",
    categories: ["animals", "ocean"],
    title: "Animals With A Plan",
    eyebrow: "Cunning",
    description:
      "Animals that scheme. Farmers, thieves, liars, strategists and engineers with no hands and no schooling - and better tactics than most people.",
    seasons: [
      {
        key: "tactics",
        title: "Hunting Tactics",
        terms: [
          "strategize", "tactic", "team hunt", "bubble", "hunt with", "hunted side-by-side",
          "play chess", "drag dinner", "flip sharks", "ambush", "coordinat", "pack hunt",
          "steal warthogs", "jaw out like a cannon", "body heat", "hunt", "fight", "rival", "squirt blood",
        ],
      },
      {
        key: "farmers",
        title: "Farmers, Builders And Engineers",
        terms: [
          "farm", "farmer", "farming", "beaver dam", "termite", "air conditioning", "build",
          "architecture", "nest", "birdhouse", "colony", "masterpiece", "ocean floor masterpieces",
        ],
      },
      {
        key: "liars",
        title: "Liars And Fakers",
        terms: [
          "pretend", "playing dead", "fake egg", "fakes its own death", "trick", "deceive",
          "disguise", "mimic", "hides its blood", "ghost stories", "steal", "thief", "gifts",
        ],
      },
      {
        key: "parents",
        title: "Parenting Like A Pro",
        terms: [
          "mom", "moms", "dad bird", "raises all the babies", "pups", "guarding their babies",
          "sneak off to give birth", "litter size", "baby kangaroo", "kin", "leave babies", "dog pregnanc",
        ],
      },
      {
        key: "senses",
        title: "Senses We Do Not Have",
        terms: [
          "sees colors", "see colors", "taste what", "smell", "echolocat", "electricity",
          "magnetic", "eyes are bigger", "squid eyes", "hears", "sea lions are secretly watching",
        ],
      },
    ],
  },
  {
    slug: "animals-that-shouldnt-exist",
    categories: ["animals", "ocean", "body"],
    title: "Animals That Shouldn't Exist",
    eyebrow: "Disbelief",
    description:
      "Bodies that break the rules of biology. No stomach, no old age, three hearts, a head that grows back. Every episode ends the same way: this is real.",
    seasons: [
      {
        key: "bodies",
        title: "Bodies That Make No Sense",
        terms: [
          "no stomach", "without stomachs", "many brains", "three hearts", "hearts live in their heads",
          "shell is part", "regrows", "melt into goo", "poop cubes", "wombat", "platypus",
          "fingerprints", "tongues wrap", "trunk", "horn", "tusk", "fluffy tails", "quills",
          "old skin", "axolotl", "kakapo", "shrimp hearts", "sweat", "skin color",
        ],
      },
      {
        key: "records",
        title: "Speed, Power And Records",
        terms: [
          "fastest", "faster than", "flies like a missile", "race car", "stronger than",
          "pack more power", "wallop", "speed defies", "roadrunner", "longer than a bus",
          "loudest", "bigger than",
        ],
        not: ["your head", "your feet", "dragonfl", "cloud"],
      },
      {
        key: "superpowers",
        title: "Survival Superpowers",
        terms: [
          "survive", "survives", "never die", "cheats death", "aging backwards", "zero water",
          "freezes solid", "frozen solid", "regenerate", "stay young", "drinks zero",
          "lives where", "wakes up", "swimmer", "never fully shut", "starve",
        ],
      },
      {
        key: "neighbours",
        title: "The Ones Living Next Door",
        terms: [
          "stray", "house cat", "raccoon", "toilet", "backyard", "birdseed", "roommate",
          "wild animal chose you", "rescuing wild animals", "deer tracker", "pay rent",
          "dog bit", "squirrel", "pet", "kid", "woodlice",
        ],
      },
      {
        key: "oddities",
        title: "Evolution's Strangest Ideas",
        terms: [
          "17 years", "underground", "cicada", "australia's animals", "weird critters",
          "blind animals", "two faces", "grew huge horns", "elephants", "cattle", "sloth",
          "rhino", "koala", "woodpecker", "butterfl", "lizard", "turtle", "monkey past",
        ],
        not: ["cloud", "your knees"],
      },
    ],
  },
  {

    slug: "ocean-deep",
    categories: ["ocean"],
    title: "The Ocean Gets Worse The Deeper You Go",
    eyebrow: "The deep",
    description:
      "Start in the shallows. End where light never arrives, pressure crushes steel and the animals stop looking like animals.",
    seasons: [
      {
        key: "abyss",
        title: "Down Where It Is Black",
        terms: [
          "deep sea", "deep-sea", "abyss", "trench", "ocean floor", "pressure", "submarine",
          "light never reaches", "midnight zone", "sunken", "shipwreck", "titanic",
        ],
      },
      {
        key: "giants",
        title: "Giants Of The Water",
        terms: [
          "whale", "sperm whale", "humpback", "beluga", "orca", "megalodon", "giant crab",
          "sea spider", "sunfish", "porpoise", "manta", "loudest animal", "jumbo jet",
        ],
      },
      {
        key: "impossible-fish",
        title: "Fish That Break The Rules",
        terms: [
          "fish", "shark", "jellyfish", "lobster", "squid", "octopus", "eel", "salmon",
          "barnacle", "shell", "seal", "dolphin", "walk and breathe", "ignore you", "fear you",
        ],
      },
      {
        key: "glow",
        title: "The Ocean Lights Up",
        terms: [
          "bioluminescen", "ocean lights up", "glow", "tiny sea forests", "plankton", "coral",
          "reef", "ghost net",
        ],
      },
      {
        key: "ocean-physics",
        title: "Impossible Water",
        terms: [
          "dead sea", "float", "gravity disappears", "underwater tunnel", "pool", "bathtub",
          "wave", "salt", "cheat crushing", "push an entire ocean", "ocean shells",
        ],
      },
    ],
  },
  {
    slug: "out-there",
    categories: ["space"],
    title: "Out There",
    eyebrow: "Cosmos",
    description:
      "Planets that would kill you in seconds, moons stranger than fiction, and the experiments we run on the universe from a rock in the suburbs of a galaxy.",
    seasons: [
      {
        key: "planets",
        title: "Planets That Punish",
        terms: [
          "venus", "mars", "mercury", "neptune", "uranus", "saturn", "jupiter", "pluto",
          "planet", "diamond rain", "rains diamonds", "dust storm",
        ],
      },
      {
        key: "moons",
        title: "Moons, Rocks And Dust",
        terms: ["moon", "moon dust", "asteroid", "meteor", "comet", "crater", "neutron star", "teaspoon"],
      },
      {
        key: "deep-cosmos",
        title: "The Universe Does Not Add Up",
        terms: [
          "black hole", "galaxy", "galaxies", "universe", "nebula", "radio signal", "cosmic",
          "solar system", "dark matter", "shouldn't exist", "beyond our comprehension",
        ],
      },
      {
        key: "space-survival",
        title: "Surviving The Vacuum",
        terms: [
          "vacuum of space", "space travel", "tardigrade", "astronaut", "space smells",
          "space isn't quiet", "survive space", "spacesuit",
        ],
      },
      {
        key: "sky",
        title: "Lights In Our Sky",
        terms: ["aurora", "borealis", "sun looks yellow", "the sun", "eclipse", "star", "sky glow"],
        not: ["neutron star", "starves"],
      },
    ],
  },
  {
    slug: "natural-wonders",
    categories: ["earth-nature", "ancient-earth", "survival-disasters"],
    title: "Natural Wonders",
    eyebrow: "Wonder",
    description:
      "The slow, beautiful, impossible parts of this planet. Stone that looks painted, forests that talk, water that glows, ice older than our species.",
    seasons: [
      {
        key: "rock",
        title: "Rock, Sand And Stone",
        terms: [
          "rock", "rocks", "desert", "canyon", "cave", "mountain", "mineral", "stone", "island",
          "walking rocks", "aussie rocks", "highest peak", "float away",
        ],
        not: ["moon", "asteroid"],
      },
      {
        key: "forests",
        title: "Forests That Talk Back",
        terms: [
          "tree", "trees", "forest", "bark", "redwood", "root", "fungal network", "plants sense",
          "plants send", "giant fungus", "mushroom", "seed", "cinnamon", "veggies are alive", "coconut",
        ],
      },
      {
        key: "ice",
        title: "Frozen Places",
        terms: [
          "ice", "snow", "snowflake", "glacier", "frozen", "arctic", "antarctic", "permafrost",
          "polar", "colder than space", "freezes solid",
        ],
      },
      {
        key: "weather-beauty",
        title: "Weather That Looks Fake",
        terms: [
          "cloud", "rainbow", "raindrop", "fog", "mirage", "waterfall", "glows like lava",
          "shadow", "crop circle", "ball lightning",
        ],
      },
      {
        key: "glow-earth",
        title: "Earth Glows In The Dark",
        terms: [
          "glowing", "you're glowing", "luminous", "purple", "colour of", "lake turns living",
          "lake stares back", "stinky flower", "agony", "lakes that can kill",
        ],
      },
      {
        key: "giants",
        title: "Giants That Really Existed",
        terms: [
          "dinosaur", "t. rex", "megalodon", "giant dragonfl", "prehistoric", "ancient skies",
          "weighed a whole ton", "gentle giant", "big ancient creatures", "rule the skies",
        ],
      },
      {
        key: "fossils",
        title: "Fossils And Museum Secrets",
        terms: [
          "fossil", "museum", "ancient bones", "monster bones", "amber", "shipwreck", "titanic",
          "pine cones", "refused to rot", "still edible", "ocean shells crown",
        ],
      },
      {
        key: "lost-earth",
        title: "Earth's Lost Ages",
        terms: [
          "million years", "billion years", "earth drowned", "extinct", "first plants",
          "giant mushrooms ruled", "great flood", "old viruses", "mongol", "ancient",
        ],
      },
    ],
  },

  {
    slug: "inside-your-body",
    categories: ["body", "mind-psychology"],
    title: "Inside Your Body",
    eyebrow: "Your body",
    description:
      "Twenty-odd things happening under your skin right now that you were never told about. Not a biology class - a tour of your own hardware.",
    seasons: [
      {
        key: "eyes",
        title: "What Your Eyes Do Not Show You",
        terms: [
          "eye", "eyes", "eyeball", "vision", "lasik", "cornea", "colors", "color", "spectrum",
          "one tiny bit of light",
        ],
        not: ["mantis shrimp", "space nebula"],
      },
      {
        key: "blood",
        title: "Blood, Guts And Air",
        terms: [
          "blood", "blood vessel", "gut", "stomach acid", "immune", "heart", "cells daily",
          "restart a heart", "belly button", "melt steel", "aging way faster", "lung", "thin air", "secretly a seal",
        ],
      },
      {
        key: "bones",
        title: "Bones And Framework",
        terms: [
          "bone", "bones", "skeleton", "tailbone", "spine", "knee", "knees", "leg", "legs",
          "stairs", "height", "shaq", "racewalker", "concrete pillars",
        ],
      },
      {
        key: "skin",
        title: "Skin, Hair And Nails",
        terms: ["hair", "nail", "nails", "skin", "sweat", "fingerprint", "hands", "grip", "cooling hat"],
      },
      {
        key: "birth",
        title: "How You Got Here",
        terms: [
          "baby", "babies", "birth", "pregnan", "dna test", "siblings", "womb", "born",
          "long shot", "tiny timing changes",
        ],
      },
    ],
  },
  {
    slug: "head-stuff",
    categories: ["mind-psychology", "body"],
    title: "Head Stuff",
    eyebrow: "Your mind",
    description:
      "Your brain is not a camera. It guesses, edits, invents and lies - and this series shows you the exact moments it does it.",
    seasons: [
      {
        key: "lies",
        title: "Your Brain Is Lying",
        terms: [
          "brain tells", "brain fakes", "fakes knowing", "brain invents", "phantom", "illusion",
          "brain's always guessing", "huge lies", "conjures noises", "phone vibrations",
        ],
      },
      {
        key: "memory",
        title: "Memory And Dreams",
        terms: [
          "memory", "memories", "dream", "dreams", "sleep", "pink noise", "tetris", "remember",
          "nose remembers",
        ],
      },
      {
        key: "fear",
        title: "Where Fear Lives",
        terms: [
          "fear", "scare", "scared", "panic", "phobia", "snake danger", "predators often ignore",
          "kids aren't scared",
        ],
      },
      {
        key: "attention",
        title: "Attention And Perception",
        terms: [
          "attention", "messy room", "textbooks", "tickle", "blindfolded", "gorillas can sneak",
          "hogs a fifth", "neurons", "just meat", "imagination",
        ],
      },
    ],
  },
  {
    slug: "the-tiny-and-terrifying",
    categories: ["animals", "body"],
    title: "The Tiny And Terrifying",
    eyebrow: "Small things",
    description:
      "Nothing here is bigger than your thumb. All of it is worse than you think, and some of it is already on your face.",
    seasons: [
      {
        key: "zombies",
        title: "Parasites And Puppeteers",
        terms: [
          "parasite", "zombie", "hairworm", "tapeworm", "host", "fungus turns", "cordyceps",
          "insect suicide", "caterpillars into zombie", "worms trick",
        ],
      },
      {
        key: "colonies",
        title: "Colonies With A Mind",
        terms: ["ant", "ants", "bee", "bees", "swarm", "hive", "cicada", "colony", "living glue"],
      },
      {
        key: "onyou",
        title: "Living On You",
        terms: ["mite", "mites", "face", "eyelash", "microbe", "bacteria", "gut flora", "while you sleep"],
      },
      {
        key: "weapons",
        title: "Tiny Things With Weapons",
        terms: [
          "quill", "pincer", "mandible", "shrimp punch", "murder hornet", "woodlice", "beetle",
          "dragonfl", "snail's sting", "porcupine",
        ],
      },
    ],
  },
  {
    slug: "everyday-impossible",
    categories: ["technology", "physics-chemistry", "earth-nature"],
    title: "Everyday Impossible",
    eyebrow: "How it works",
    description:
      "The physics and engineering hiding in ordinary life: buildings that flex, water that misbehaves, materials that should not work, and machines quietly replacing us.",
    seasons: [
      {
        key: "buildings",
        title: "Buildings And Machines",
        terms: [
          "skyscraper", "building", "stadium", "aircraft carrier", "bridge", "tunnel", "faucet",
          "eiffel", "invisible walls", "plane crash", "metal detector", "roof",
        ],
      },
      {
        key: "matter",
        title: "Matter Behaving Badly",
        terms: [
          "boils", "freezes faster", "helium", "mercury's electrons", "spooky connections",
          "quantum", "shadows can outrun", "physics of", "purr", "schrödinger",
        ],
      },
      {
        key: "materials",
        title: "Materials That Should Not Work",
        terms: [
          "spider silk", "steel", "grease", "banana peel", "sticky", "goalkeeper gloves",
          "concrete", "silk beats", "gold underground",
        ],
      },
      {
        key: "robots",
        title: "Machines Taking Over",
        terms: ["robot", "robots", "ai ", "artificial intelligence", "automat", "picking jobs", "restaurant job"],
      },
      {
        key: "odd-jobs",
        title: "Jobs Nobody Explains",
        terms: [
          "airplane poop", "motogp", "ear protection", "string plants seeds", "wells", "grass fields",
          "racers", "steer from both ends",
        ],
      },
    ],
  },
];

export const SERIES_BY_SLUG: Record<string, SeriesDef> = Object.fromEntries(
  SERIES.map((s) => [s.slug, s]),
);

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ");
}

export type Classifiable = { title: string; excerpt?: string | null; categorySlug?: string | null };

/**
 * The stored category is sometimes wrong (a whale article filed under "body",
 * a museum article filed under "animals"). The headline is the honest signal,
 * so it wins. Order matters: human first, then time, sky, water, wildlife.
 */
const CATEGORY_OVERRIDES: Array<{ test: RegExp; category: string }> = [
  // A headline that OPENS with an animal is an animal story, whatever body
  // part it goes on to mention ("Pythons hunt by seeing your body heat").
  { test: /^(whales?|sharks?|dolphins?|orcas?|seals?|octopuses|squids?|jellyfish|lobsters?|crabs?|sea \w+|fish)\b/, category: "ocean" },
  { test: /^(pythons?|crocs?|crocodiles?|sloths?|horses?|lizards?|these lizards|snakes?|baby elephants|reindeer|lionesses|magpies|shrimps?|ants?|bees?|mites?|dogs?|cats?|birds?|frogs?|koalas?|kangaroos?|wombats?|platypuses|axolotls?|alligators?|cicadas?|butterflies|woodlice|raccoons?|squirrels?|foxes|wolves|leopards?|lions?|tigers?|jaguars?|eagles?|crows?|owls?|ostriches|vultures?|rhinos?|antelopes?|deer|cattle|cows?|porcupines?|snails?|tapeworms?|hairworms?|termites?|beavers?)\b/, category: "animals" },
  { test: /^clouds/, category: "earth-nature" },
  { test: /schrodinger|quantum|helium|electrons|physics of/, category: "physics-chemistry" },
  { test: /shipwrecks?|titanic|light never reaches|jumbo jet/, category: "ocean" },
  { test: /\byour (brain|memory|mind|dreams?)\b|\bbrain\b|\bmemory\b|\bdreams?\b|tickle|messy room|textbooks|blindfolded/, category: "mind-psychology" },
  { test: /\byour (eyes?|eyeballs?|blood|gut|stomach|hair|nails?|skin|heart|bones?|legs?|knees?|hands?|nose|tailbone|immune|belly|head|body|lungs|siblings|feet|cells)\b|human lives|lasik|racewalker|\bshaq\b|baby s grip|babies hands|having a baby|baby picks|dna test|thin air fools/, category: "body" },
  { test: /museum|fossil|dinosaur|t rex|prehistoric|megalodon|million years|\bancient\b|mongol|refused to rot|primeval/, category: "ancient-earth" },
  { test: /venus|mars|neptune|saturn|jupiter|pluto|\bmoon\b|galax|nebula|black hole|asteroid|neutron star|solar system|universe|aurora|vacuum of space|space travel|space smells|space isn t|space is loud|tardigrade|radio signal/, category: "space" },
  { test: /whale|shark|jellyfish|octopus|squid|\bfish\b|\bseals?\b|dolphin|orca|\bcrabs?\b|lobster|barnacle|porpoise|sunfish|coral|reef|\bocean\b|tsunami|submarine|sea spider|mermaid/, category: "ocean" },
  { test: /lion|tiger|leopard|jaguar|snake|cobra|mamba|\bbirds?\b|eagle|crow|owl|kangaroo|koala|wombat|elephant|horse|\bdogs?\b|\bcats?\b|frog|lizard|\bants?\b|\bbees?\b|wasp|hornet|spider|beetle|butterfl|cicada|reindeer|magpie|\bfox\b|wolf|raccoon|squirrel|platypus|sloth|rhino|antelope|goat|turtle|axolotl|woodpecker|porcupine|snail|worm|mites?|termite|deer|cattle|\bcows?\b|kitten|parrot|ostrich|python|croc|alligator|vulture|shrimp|cassowary|beaver|polar bear/, category: "animals" },
];

/** Category we trust for classification, headline first. */
export function effectiveCategory(item: Classifiable): string | null {
  const title = normalize(item.title);
  for (const rule of CATEGORY_OVERRIDES) if (rule.test.test(title)) return rule.category;
  return item.categorySlug ?? null;
}

export type Assignment = { seriesSlug: string; seasonIndex: number; score: number };

/**
 * Score every season across every series and return the single best fit.
 * Deterministic: same input always produces the same season.
 */
export function classify(item: Classifiable): Assignment | null {
  const text = normalize(`${item.title} ${item.excerpt ?? ""}`);
  const titleText = normalize(item.title);
  let best: Assignment | null = null;

  SERIES.forEach((series) => {
    // The article's own category is the strongest signal we have: it stops a
    // body article landing in an animal season and vice versa.
    const cat = effectiveCategory(item);
    const catBonus = cat
      ? series.categories.includes(cat)
        ? 5
        : -6
      : 0;
    series.seasons.forEach((season, seasonIndex) => {
      if (season.not?.some((n) => text.includes(normalize(n)))) return;
      let score = 0;
      for (const term of season.terms) {
        const t = normalize(term).trim();
        if (!t) continue;
        const phrase = t.includes(" ");
        const word = new RegExp(`\\b${t}(s|es)?\\b`);
        const hit = phrase ? text.includes(t) : word.test(text);
        if (!hit) continue;
        score += phrase ? 3 : 1;
        // A match in the headline is what the reader actually sees.
        const titleHit = phrase ? titleText.includes(t) : word.test(titleText);
        if (titleHit) score += phrase ? 3 : 2;
      }
      if (score <= 0) return;
      score += catBonus;
      if (score <= 0) return;
      if (!best || score > best.score) best = { seriesSlug: series.slug, seasonIndex, score };
    });
  });

  return best;
}

/** position -> { seasonIndex, episode } */
export function decodePosition(position: number): { seasonIndex: number; episode: number } {
  return {
    seasonIndex: Math.floor(position / SEASON_STRIDE),
    episode: position % SEASON_STRIDE,
  };
}

export function encodePosition(seasonIndex: number, episode: number): number {
  return seasonIndex * SEASON_STRIDE + episode;
}

/** Season label for a stored position inside a given series. */
export function seasonTitle(seriesSlug: string, seasonIndex: number): string {
  if (seasonIndex === BEST_OF_SEASON) return "Best Of The Month";
  return SERIES_BY_SLUG[seriesSlug]?.seasons[seasonIndex]?.title ?? "More";
}
